import DoneIcon from '@mui/icons-material/Done'
import { TableCell, TableRow } from '@mui/material'
import Link from '@mui/material/Link'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RRLink, useLocation } from 'react-router-dom'
import { TableLabelText, TablePagination } from '~/src/components/molecules'
import { TableListContainer } from '~/src/components/organisms'
import { IPagination } from '~/src/repositories/User/models/User'
import {
  StyledBox,
  StyledBoxLabel,
  StyledChip,
  StyledTableCell,
  StyledTypographyHeadLabel,
  StyledTypographyHeadHyphen,
  StyledPaper,
} from './Members.styles'
import { formatUnixTimeStampToDate } from '~/src/libs'

interface IMemberItem {
  id: string
  name: string
  phoneNumber: number
  country: string
  companyName: string
  email: string
  registerStatus: string
  createdAt: number
}

interface IColumn {
  id:
    | 'checked'
    | 'companyName'
    | 'name'
    | 'country'
    | 'phoneNumber'
    | 'email'
    | 'registerStatus'
    | 'loadingPort'
    | 'registeredDate'
    | 'quotationDetail'
  label: string | React.ReactNode
  minWidth?: number
  align?: 'right'
  render: (item: IMemberItem, index: number) => React.ReactNode
}

interface IGetUserParams {
  offset?: number
  limit?: number
  countryId?: string
  email?: string
  name?: string
}
interface IMemberProps {
  users: IMemberItem[]
  pagination: IPagination
  onSubmitGetUser: (_params?: IGetUserParams) => void
}

export const Members: React.FC<IMemberProps> = ({
  users,
  pagination,
  onSubmitGetUser,
}): JSX.Element => {
  const { t } = useTranslation()
  const location = useLocation()
  const [page, setPage] = useState(pagination?.currentPage - 1)
  const [rowsPerPage, setRowsPerPage] = useState(pagination?.limitValue)

  const columns: IColumn[] = [
    {
      id: 'companyName',
      label: `${t('list_member.company_name')}`,
      minWidth: 150,
      render: (item) => (
        <TableLabelText>
          <Link
            to={`/admin/member/detail/${item.id}`}
            state={{ prevLocationState: { ...location } }}
            underline="always"
            component={RRLink}
          >
            {item.companyName}
          </Link>
        </TableLabelText>
      ),
    },
    {
      id: 'name',
      label: `${t('list_member.name')}`,
      minWidth: 200,
      render: ({ name }) => <TableLabelText>{name}</TableLabelText>,
    },
    {
      id: 'country',
      label: `${t('list_member.country')}`,
      minWidth: 200,
      render: ({ country }) => <TableLabelText>{country}</TableLabelText>,
    },
    {
      id: 'phoneNumber',
      label: `${t('list_member.phone_number')}`,
      minWidth: 150,
      render: ({ phoneNumber }) => <TableLabelText>{phoneNumber}</TableLabelText>,
    },
    {
      id: 'email',
      label: `${t('list_member.email')}`,
      minWidth: 200,
      render: (item) => (
        <TableLabelText>
          <Link
            to={`/admin/member/detail/${item.id}`}
            state={{ prevLocationState: { ...location } }}
            underline="always"
            component={RRLink}
          >
            {item.email}
          </Link>
        </TableLabelText>
      ),
    },
    {
      id: 'registerStatus',
      label: `${t('list_member.register_status')}`,
      minWidth: 130,
      render: ({ registerStatus }) =>
        registerStatus ? (
          <StyledChip
            icon={<DoneIcon sx={{ color: '#666666', fontSize: '13px' }} />}
            label={`${t('list_member.contracted')}`}
            variant="outlined"
          />
        ) : null,
    },
    {
      id: 'loadingPort',
      label: `${t('list_member.loading_port')}`,
      minWidth: 130,
      render: ({ loadingPort }) => <TableLabelText>{loadingPort}</TableLabelText>,
    },
    {
      id: 'registeredDate',
      label: `${t('list_member.registered_date')}`,
      minWidth: 130,
      render: ({ createdAt }) =>
      <TableLabelText>
        {createdAt && formatUnixTimeStampToDate(
          createdAt as unknown as number,
          'yyyy/MM/dd h:mm:ss'
        )}
      </TableLabelText>,
    },
    {
      id: 'quotationDetail',
      label: `${t('list_member.quotation_detail')}`,
      minWidth: 130,
      render: ({ id }) => 
        <TableLabelText>
          <Link
            to={`/admin/member/quotation/${id}`}
            state={{ prevLocationState: { ...location } }}
            underline="always"
            component={RRLink}
          >
            {t('list_member.quotation_detail')}
          </Link>
        </TableLabelText>,
    },
  ]
  const handleChangePage = (event: unknown, newPage: number): void => {
    setPage(newPage)
    onSubmitGetUser({ offset: newPage + 1, limit: 20 })
  }

  return (
    <StyledPaper>
      <StyledBox>
        <TableListContainer
          tableHeadComponents={columns.map((column, index) => (
            <TableCell
              key={column.id}
              style={{ minWidth: column.minWidth, background: '#FFFFFF', padding: '16px 8px' }}
            >
              <StyledBoxLabel>
                <StyledTypographyHeadLabel>{column.label}</StyledTypographyHeadLabel>
                <StyledTypographyHeadHyphen
                  sx={{ backgroundColor: `${index === columns.length - 1 ? 'white' : '#E0E0E0'}` }}
                />
              </StyledBoxLabel>
            </TableCell>
          ))}
          tableBodyComponents={users?.map((item, index) => {
            return (
              <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                {columns.map((column, i) => {
                  return (
                    <StyledTableCell key={i} align={column.align}>
                      {column.render(item, index)}
                    </StyledTableCell>
                  )
                })}
              </TableRow>
            )
          })}
          tableContainerSx={null}
        />
      </StyledBox>
      <TablePagination
        count={pagination?.totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
      />
    </StyledPaper>
  )
}
